import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

// Same-origin: nginx (prod) and the Vite dev proxy both route /api to the
// backend. Absolute http:// URLs would be blocked as mixed content over HTTPS.
const API_URL = window.location.origin;

// Async thunks for search
export const advancedSearch = createAsyncThunk(
	"search/advanced",
	async (searchRequest) => {
		const response = await api.post(
			`${API_URL}/api/search/advanced`,
			searchRequest,
		);
		return response.data;
	},
);

export const quickSearch = createAsyncThunk(
	"search/quick",
	async ({ query, workspaceId }) => {
		const response = await api.get(`${API_URL}/api/search/quick`, {
			params: { query, workspaceId },
		});
		return response.data;
	},
);

export const searchByTags = createAsyncThunk(
	"search/byTags",
	async ({ tags, matchMode, workspaceId }) => {
		const response = await api.get(`${API_URL}/api/search/tags`, {
			params: { tags, matchMode, workspaceId },
		});
		return response.data;
	},
);

export const getRecentlyExecuted = createAsyncThunk(
	"search/recentExecuted",
	async ({ workspaceId, limit }) => {
		const response = await api.get(
			`${API_URL}/api/search/recent/executed`,
			{
				params: { workspaceId, limit },
			},
		);
		return response.data;
	},
);

export const getRecentlyCreated = createAsyncThunk(
	"search/recentCreated",
	async ({ workspaceId, limit }) => {
		const response = await api.get(`${API_URL}/api/search/recent/created`, {
			params: { workspaceId, limit },
		});
		return response.data;
	},
);

export const getFailingTests = createAsyncThunk(
	"search/failing",
	async (workspaceId) => {
		const response = await api.get(`${API_URL}/api/search/failing`, {
			params: { workspaceId },
		});
		return response.data;
	},
);

export const getUnstableTests = createAsyncThunk(
	"search/unstable",
	async ({ workspaceId, successRateThreshold }) => {
		const response = await api.get(`${API_URL}/api/search/unstable`, {
			params: { workspaceId, successRateThreshold },
		});
		return response.data;
	},
);

export const getAllTags = createAsyncThunk(
	"search/allTags",
	async (workspaceId) => {
		const response = await api.get(`${API_URL}/api/search/tags/all`, {
			params: { workspaceId },
		});
		return response.data;
	},
);

// Async thunks for saved views
export const fetchUserViews = createAsyncThunk(
	"search/fetchUserViews",
	async (userId) => {
		const response = await api.get(`${API_URL}/api/views/user/${userId}`);
		return response.data;
	},
);

export const fetchSharedViews = createAsyncThunk(
	"search/fetchSharedViews",
	async (workspaceId) => {
		const response = await api.get(
			`${API_URL}/api/views/shared/${workspaceId}`,
		);
		return response.data;
	},
);

export const createSavedView = createAsyncThunk(
	"search/createView",
	async (view) => {
		const response = await api.post(`${API_URL}/api/views`, view);
		return response.data;
	},
);

export const updateSavedView = createAsyncThunk(
	"search/updateView",
	async ({ id, view }) => {
		const response = await api.put(`${API_URL}/api/views/${id}`, view);
		return response.data;
	},
);

export const deleteSavedView = createAsyncThunk(
	"search/deleteView",
	async (id) => {
		await api.delete(`${API_URL}/api/views/${id}`);
		return id;
	},
);

export const incrementViewUseCount = createAsyncThunk(
	"search/incrementViewUse",
	async (id) => {
		await api.post(`${API_URL}/api/views/${id}/use`);
		return id;
	},
);

// Slice
const searchSlice = createSlice({
	name: "search",
	initialState: {
		results: [],
		savedViews: [],
		allTags: [],
		currentFilters: null,
		currentSettings: null,
		loading: false,
		error: null,
	},
	reducers: {
		clearResults: (state) => {
			state.results = [];
		},
		setFilters: (state, action) => {
			state.currentFilters = action.payload;
		},
		setSettings: (state, action) => {
			state.currentSettings = action.payload;
		},
		clearError: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			// Advanced Search
			.addCase(advancedSearch.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(advancedSearch.fulfilled, (state, action) => {
				state.loading = false;
				state.results = action.payload;
			})
			.addCase(advancedSearch.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Quick Search
			.addCase(quickSearch.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(quickSearch.fulfilled, (state, action) => {
				state.loading = false;
				state.results = action.payload;
			})
			.addCase(quickSearch.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})

			// Search by Tags
			.addCase(searchByTags.fulfilled, (state, action) => {
				state.results = action.payload;
			})

			// Recently Executed
			.addCase(getRecentlyExecuted.fulfilled, (state, action) => {
				state.results = action.payload;
			})

			// Recently Created
			.addCase(getRecentlyCreated.fulfilled, (state, action) => {
				state.results = action.payload;
			})

			// Failing Tests
			.addCase(getFailingTests.fulfilled, (state, action) => {
				state.results = action.payload;
			})

			// Unstable Tests
			.addCase(getUnstableTests.fulfilled, (state, action) => {
				state.results = action.payload;
			})

			// All Tags
			.addCase(getAllTags.fulfilled, (state, action) => {
				state.allTags = action.payload;
			})

			// Fetch User Views
			.addCase(fetchUserViews.fulfilled, (state, action) => {
				state.savedViews = action.payload;
			})

			// Fetch Shared Views
			.addCase(fetchSharedViews.fulfilled, (state, action) => {
				state.savedViews = [
					...state.savedViews,
					...action.payload.filter(
						(view) =>
							!state.savedViews.find((v) => v.id === view.id),
					),
				];
			})

			// Create Saved View
			.addCase(createSavedView.fulfilled, (state, action) => {
				state.savedViews.push(action.payload);
			})

			// Update Saved View
			.addCase(updateSavedView.fulfilled, (state, action) => {
				const index = state.savedViews.findIndex(
					(v) => v.id === action.payload.id,
				);
				if (index !== -1) {
					state.savedViews[index] = action.payload;
				}
			})

			// Delete Saved View
			.addCase(deleteSavedView.fulfilled, (state, action) => {
				state.savedViews = state.savedViews.filter(
					(v) => v.id !== action.payload,
				);
			});
	},
});

export const { clearResults, setFilters, setSettings, clearError } =
	searchSlice.actions;
export default searchSlice.reducer;
